import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement} viewport
 * @property {HTMLElement[] | undefined} panels
 * @property {HTMLElement | undefined} mediaFrame
 * @property {HTMLElement | undefined} indicator
 * @property {HTMLElement[] | undefined} mediaItems
 * @property {HTMLElement[] | undefined} indicatorItems
 */

/**
 * Sticky scroll section.
 *
 * @extends Component<Refs>
 */
class StickyScroll extends Component {
  requiredRefs = ['track', 'viewport'];

  #activeIndex = 0;
  #isIntersecting = false;
  #raf = 0;
  #resizeObserver = null;
  #intersectionObserver = null;
  #pendingJumpIndex = null;
  #pendingJumpTargetY = 0;
  #pendingJumpTimeout = 0;
  #mobileQuery = matchMedia('(max-width: 989px)');
  #reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.#rehydrate();

    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      this.#rehydrate();
    });

    this.#mobileQuery.addEventListener('change', this.#handleModeChange);
    this.#reducedMotionQuery.addEventListener('change', this.#handleModeChange);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#teardown();
    this.#mobileQuery.removeEventListener('change', this.#handleModeChange);
    this.#reducedMotionQuery.removeEventListener('change', this.#handleModeChange);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  updatedCallback() {
    this.#rehydrate();
  }

  #rehydrate() {
    this.#teardown();
    this.#hydrateMediaAndIndicator();
    super.updatedCallback();
    this.#setup();
  }

  #getPanels() {
    return Array.isArray(this.refs.panels) ? this.refs.panels : [];
  }

  #hydrateMediaAndIndicator() {
    const panels = Array.from(this.querySelectorAll('.sticky-scroll__panel'));
    const mediaFrame = this.querySelector('[ref="mediaFrame"]');

    this.dataset.panelCount = String(panels.length);

    if (mediaFrame instanceof HTMLElement && panels.length) {
      mediaFrame.innerHTML = '';

      panels.forEach((panel, index) => {
        const source = panel.querySelector('.sticky-scroll__panel-media-source');
        const item = document.createElement('div');

        item.className = `sticky-scroll__media-item${index === 0 ? ' is-active' : ''}`;
        item.setAttribute('ref', 'mediaItems[]');
        item.dataset.mediaIndex = String(index);

        if (index !== 0) {
          item.setAttribute('aria-hidden', 'true');
        }

        if (source) {
          item.append(...Array.from(source.childNodes).map((node) => node.cloneNode(true)));
        }

        mediaFrame.append(item);
      });
    }

    const indicator = this.querySelector('[ref="indicator"]');

    if (indicator instanceof HTMLElement) {
      indicator.innerHTML = '';

      if (this.dataset.showIndicator === 'true' && panels.length > 1) {
        panels.forEach((_, index) => {
          const item = document.createElement(this.dataset.enableIndicatorJump === 'true' ? 'button' : 'span');

          item.className = `sticky-scroll__indicator-item${index === 0 ? ' is-active' : ''}`;
          item.setAttribute('ref', 'indicatorItems[]');
          item.dataset.indicatorIndex = String(index);

          if (item instanceof HTMLButtonElement) {
            item.type = 'button';
            item.setAttribute('aria-label', `Show panel ${index + 1}`);
          }

          if (index === 0) {
            item.setAttribute('aria-current', 'true');
          }

          indicator.append(item);
        });
      }
    }
  }
  
  #setup() {
    const panels = this.#getPanels();

    if (!panels.length) return;

    if (!this.#canPin()) {
      this.#setFallback();
      return;
    }

    this.dataset.ready = 'true';

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.refs.viewport);
    this.#resizeObserver.observe(this.refs.track);
    this.#resizeObserver.observe(this.refs.viewport.querySelector('.sticky-scroll__layout') || this.refs.viewport);

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isIntersecting = Boolean(entry?.isIntersecting);

        if (this.#isIntersecting) {
          this.#queueUpdate();
        }
      },
      { threshold: 0 }
    );

    this.#intersectionObserver.observe(this);

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('scrollend', this.#handleScrollEnd);
    window.addEventListener('resize', this.#handleResize, { passive: true });

    this.refs.indicatorItems?.forEach((item) => {
      item.addEventListener('click', this.#handleIndicatorClick);
      item.addEventListener('keydown', this.#handleIndicatorKeydown);
    });

    this.#measure();
    this.#setActive(0);
    this.#queueUpdate();
  }

  #teardown() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('scrollend', this.#handleScrollEnd);
    window.removeEventListener('resize', this.#handleResize);

    this.refs.indicatorItems?.forEach((item) => {
      item.removeEventListener('click', this.#handleIndicatorClick);
      item.removeEventListener('keydown', this.#handleIndicatorKeydown);
    });

    if (this.#raf) {
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }

    this.#clearPendingJump();

    delete this.dataset.ready;
  }

  #canPin() {
    const isStackedMobile = this.dataset.mobileLayout === 'stacked' && this.#mobileQuery.matches;

    return (
      this.dataset.enableSticky === 'true' &&
      this.#getPanels().length > 1 &&
      !isStackedMobile &&
      !prefersReducedMotion()
    );
  }

  #setFallback() {
    this.#teardown();

    this.#getPanels().forEach((panel, index) => {
      panel.dataset.active = 'true';
      panel.removeAttribute('aria-hidden');

      if (index === 0) {
        panel.classList.add('is-active');
      } else {
        panel.classList.remove('is-active');
      }
    });

    this.refs.mediaItems?.forEach((item, index) => {
      item.classList.toggle('is-active', index === 0);
      item.toggleAttribute('aria-hidden', index !== 0);
    });

    this.refs.indicatorItems?.forEach((item, index) => {
      item.classList.toggle('is-active', index === 0);
      item.toggleAttribute('aria-current', index === 0);
    });
  }

  #measure = () => {
    const maxPanelHeight = this.#getPanels().reduce((height, panel) => {
      const previousActive = panel.dataset.active;
      panel.dataset.active = 'true';
      const panelHeight = panel.offsetHeight;
      panel.dataset.active = previousActive ?? 'false';

      return Math.max(height, panelHeight);
    }, 0);

    if (maxPanelHeight > 0) {
      this.style.setProperty('--sticky-scroll-panel-height', `${maxPanelHeight}px`);
    }

    const layout = this.refs.viewport.querySelector('.sticky-scroll__layout');
    let layoutHeight = this.refs.viewport.offsetHeight;

    if (layout instanceof HTMLElement) {
      layoutHeight = layout.offsetHeight;
      this.style.setProperty('--sticky-scroll-layout-height', `${layoutHeight}px`);
    }

    this.#setTrackHeight(layoutHeight);
  };

  #setTrackHeight(layoutHeight = this.refs.viewport.offsetHeight) {
    const scrollLength = Number(this.dataset.scrollLength) || 90;
    const panelCount = this.#getPanels().length;
    const transitionCount = Math.max(panelCount - 1, 0);
    const safeLayoutHeight = Math.max(layoutHeight, 1);
    const transitionDistance = Math.max(120, safeLayoutHeight * (scrollLength / 100) * 0.6);
    const endScrollDistance = this.#getEndScrollDistance(safeLayoutHeight);

    this.style.setProperty(
      '--sticky-scroll-track-height',
      `${Math.ceil(safeLayoutHeight + transitionCount * transitionDistance + endScrollDistance)}px`
    );
  }

  #getEndScrollDistance(layoutHeight = this.refs.viewport.offsetHeight) {
    const endScrollSpace = Number(this.dataset.endScrollSpace) || 0;
    const safeLayoutHeight = Math.max(layoutHeight, 1);

    return Math.max(0, safeLayoutHeight * (endScrollSpace / 100));
  }

  #getStickyOffset() {
    const offset = Number.parseFloat(getComputedStyle(this.refs.viewport).top);

    return Number.isFinite(offset) ? Math.max(offset, 0) : 0;
  }

  #handleModeChange = () => {
    this.#rehydrate();
  };

  #handleScroll = () => {
    if (!this.#isIntersecting) return;
    this.#queueUpdate();
  };

  #handleResize = debounce(() => {
    if (!this.#canPin()) {
      this.#setFallback();
      return;
    }

    this.#measure();
    this.#queueUpdate();
  }, 150);

  #queueUpdate() {
    if (this.#raf) return;

    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#updateFromScroll();
    });
  }

  #updateFromScroll() {
    if (!this.#canPin()) return;

    const panels = this.#getPanels();
    const rect = this.refs.track.getBoundingClientRect();
    const viewportHeight = this.refs.viewport.offsetHeight || window.innerHeight;
    const endScrollDistance = this.#getEndScrollDistance(viewportHeight);
    const scrollDistance = Math.max(this.refs.track.offsetHeight - viewportHeight - endScrollDistance, 1);
    const stickyOffset = this.#getStickyOffset();
    const progress = Math.min(Math.max((stickyOffset - rect.top) / scrollDistance, 0), 1);
    const index = Math.min(panels.length - 1, Math.floor(progress * panels.length));

    if (this.#pendingJumpIndex !== null) {
      if (Math.abs(window.scrollY - this.#pendingJumpTargetY) > 2) {
        this.#setActive(this.#pendingJumpIndex);
        return;
      }

      this.#clearPendingJump();
    }

    this.#setActive(index);
  }

  #setActive(index) {
    const panels = this.#getPanels();

    if (index < 0 || index >= panels.length) return;

    this.#activeIndex = index;

    panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.dataset.active = isActive ? 'true' : 'false';
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('aria-hidden', !isActive && this.hasAttribute('data-ready'));
    });

    this.refs.mediaItems?.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('is-active', isActive);
      item.toggleAttribute('aria-hidden', !isActive);
    });

    this.refs.indicatorItems?.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('is-active', isActive);

      if (isActive) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  #handleIndicatorClick = (event) => {
    if (this.dataset.enableIndicatorJump !== 'true') return;

    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    this.#jumpToIndex(Number(target.dataset.indicatorIndex));
  };

  #handleIndicatorKeydown = (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.#handleIndicatorClick(event);
  };

  #jumpToIndex(index) {
    const panels = this.#getPanels();

    if (!Number.isInteger(index) || index < 0 || index >= panels.length) return;

    this.#setActive(index);

    if (!this.#canPin()) {
      panels[index]?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      return;
    }

    const viewportHeight = this.refs.viewport.offsetHeight || window.innerHeight;
    const endScrollDistance = this.#getEndScrollDistance(viewportHeight);
    const scrollDistance = Math.max(this.refs.track.offsetHeight - viewportHeight - endScrollDistance, 1);
    const panelProgress = (index + 0.5) / panels.length;
    const targetY =
      window.scrollY +
      this.refs.track.getBoundingClientRect().top -
      this.#getStickyOffset() +
      scrollDistance * panelProgress;

    this.#holdActiveDuringJump(index, targetY);

    window.scrollTo({
      top: targetY,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #holdActiveDuringJump(index, targetY) {
    this.#clearPendingJump();

    this.#pendingJumpIndex = index;
    this.#pendingJumpTargetY = targetY;
    this.#pendingJumpTimeout = window.setTimeout(this.#handleScrollEnd, 1200);
  }

  #clearPendingJump() {
    if (this.#pendingJumpTimeout) {
      window.clearTimeout(this.#pendingJumpTimeout);
      this.#pendingJumpTimeout = 0;
    }

    this.#pendingJumpIndex = null;
    this.#pendingJumpTargetY = 0;
  }

  #handleScrollEnd = () => {
    if (this.#pendingJumpIndex === null) return;

    this.#clearPendingJump();
    this.#queueUpdate();
  };

  #handleBlockSelect = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const panel = event.target.closest('.sticky-scroll__panel');
    if (!panel || !this.contains(panel)) return;

    const panels = this.#getPanels();
    const index = panels.indexOf(panel);
    if (index === -1) return;

    this.#setActive(index);

    if (this.#canPin()) {
      this.#jumpToIndex(index);
    } else {
      panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
}

if (!customElements.get('sticky-scroll')) {
  customElements.define('sticky-scroll', StickyScroll);
}
